import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
    bookingsByDay,
    CalendarBooking,
    monthWeeks,
} from "../../domain/calendarGrid";
import { bookingCardStyle } from "./bookingCardStyle";

/** How one booking is drawn in its day: what it says and in which colour. */
export interface MonthAgendaCard {
    key: string;
    /** What follows the start time on the card, e.g. who it is with. */
    text: string;
    /** The whole booking in words, for the tooltip. */
    title: string;
    ariaLabel: string;
    /** A hex colour, `#rrggbb`. */
    color: string;
    past: boolean;
    cancelled: boolean;
}

interface MonthAgendaGridProps<T extends CalendarBooking> {
    /** Any day of the month on screen, `YYYY-MM-DD`. */
    anchor: string;
    todayKey: string;
    bookings: readonly T[];
    cardOf: (booking: T) => MonthAgendaCard;
    onOpen: (booking: T) => void;
    /** A cell with more than it can show offers the rest: usually its week. */
    onShowMore: (day: string) => void;
    moreLabel: (count: number) => string;
    /** A click on a day's free space, e.g. to book on it. Off when absent. */
    onDayClick?: (day: string) => void;
    dayClickLabel?: (day: string) => string;
}

/** What a month cell lists before it says how many more there are. */
const MONTH_CELL_LIMIT = 3;

/**
 * A month as a wall calendar: Monday-first weeks, each day listing its first
 * few bookings as coloured cards and how many more it holds.
 *
 * The centre agenda and the therapist's scheduler both draw their month with
 * it, so a month reads the same wherever somebody opens one.
 */
export default function MonthAgendaGrid<T extends CalendarBooking>({
    anchor,
    todayKey,
    bookings,
    cardOf,
    onOpen,
    onShowMore,
    moreLabel,
    onDayClick,
    dayClickLabel,
}: MonthAgendaGridProps<T>) {
    const { i18n } = useTranslation();
    const weeks = monthWeeks(anchor);
    const byDay = useMemo(() => bookingsByDay(bookings), [bookings]);

    const weekHeaders = Array.from({ length: 7 }, (_, index) =>
        // 2024-01-01 was a Monday, so this yields a Monday-first week.
        new Date(2024, 0, 1 + index).toLocaleDateString(i18n.language, { weekday: "short" })
    );

    return (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-white/10">
            {/* A phone gets seven narrow columns whose cards keep at least the
                time, rather than a month to scroll sideways. */}
            <div className="sm:min-w-[640px]">
                <div className="grid grid-cols-7 border-b border-gray-200 dark:border-white/10">
                    {weekHeaders.map((label) => (
                        <div
                            key={label}
                            className="py-2 text-center text-xs font-medium uppercase tracking-wide text-content-tertiary"
                        >
                            {label}
                        </div>
                    ))}
                </div>
                {weeks.map((week) => (
                    <div
                        key={week.find(Boolean) ?? ""}
                        className="grid grid-cols-7 border-b border-gray-100 last:border-0 dark:border-white/5"
                    >
                        {week.map((day, index) => {
                            if (!day) {
                                return (
                                    <div
                                        key={`empty-${index}`}
                                        className="min-h-28 border-l border-gray-100 bg-gray-50/50 first:border-0 dark:border-white/5 dark:bg-white/[0.02]"
                                    />
                                );
                            }
                            const dayBookings = byDay.get(day) ?? [];
                            const shown = dayBookings.slice(0, MONTH_CELL_LIMIT);
                            const hidden = dayBookings.length - shown.length;

                            return (
                                <div
                                    key={day}
                                    data-day={day}
                                    className="relative flex min-h-28 flex-col gap-1 border-l border-gray-100 p-1.5 first:border-0 dark:border-white/5"
                                >
                                    {/* The free space of the day, under its cards. */}
                                    {onDayClick && (
                                        <button
                                            type="button"
                                            onClick={() => onDayClick(day)}
                                            aria-label={dayClickLabel?.(day) ?? day}
                                            className="absolute inset-0 transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.03]"
                                        />
                                    )}
                                    <span
                                        className={`pointer-events-none relative flex h-6 w-6 items-center justify-center self-end rounded-full text-xs font-semibold ${
                                            day === todayKey
                                                ? "bg-brand-600 text-white"
                                                : "text-gray-700 dark:text-gray-200"
                                        }`}
                                    >
                                        {Number(day.slice(8, 10))}
                                    </span>
                                    {shown.map((booking) => {
                                        const card = cardOf(booking);
                                        return (
                                            <button
                                                key={card.key}
                                                type="button"
                                                data-past={card.past || undefined}
                                                onClick={() => onOpen(booking)}
                                                title={card.title}
                                                aria-label={card.ariaLabel}
                                                className={`relative truncate rounded border-l-4 bg-white px-1.5 py-0.5 text-left text-xs leading-tight transition-[filter] hover:brightness-95 dark:bg-gray-900 ${
                                                    card.past ? "text-content-tertiary" : "text-content-primary"
                                                } ${card.cancelled ? "line-through" : ""}`}
                                                style={bookingCardStyle(card.color, card.past)}
                                            >
                                                <span className="font-semibold tabular-nums">
                                                    {booking.hour.slice(0, 5)}
                                                </span>{" "}
                                                {card.text}
                                            </button>
                                        );
                                    })}
                                    {hidden > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => onShowMore(day)}
                                            className="relative self-start rounded px-1.5 text-left text-xs font-medium text-brand-600 hover:underline dark:text-brand-300"
                                        >
                                            {moreLabel(hidden)}
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
}
